import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Skeleton,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Slider,
  Switch,
  FormControlLabel,
  Collapse,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Sort as SortIcon,
  FilterList as FilterListIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Tune as TuneIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  Psychology as PsychologyIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShoppingCart as ShoppingCartIcon,
  HomeWork as HomeWorkIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearch } from '../../context/SearchContext';
import { useTranslation } from 'react-i18next';
import useSearchOptimization from '../../hooks/useSearchOptimization';
import SearchResultCard from './SearchResultCard';
import { PRODUCT_CONDITION_OPTIONS } from '../../constants/productConditions';

const SearchResultsList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['common', 'products']);
  
  const {
    results,
    isLoading,
    error,
    sortBy,
    sortOrder,
    sortResults,
    loadMore,
    canLoadMore,
    selectListing,
    selectedListing,
    selectedListingSource,
    // Produits
    productResults,
    searchType,
    hasProductResults,
    filters,
    updateFilters
  } = useSearch();

  // Refs pour le scroll auto vers les cards sélectionnées depuis la carte
  const cardRefs = useRef({});

  // Hook d'optimisation pour les fonctionnalités avancées
  const {
    trackInteraction,
    realtimeMetrics,
    hasRealtimeMetrics
  } = useSearchOptimization();

  // États locaux avancés
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'compact'
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  const [virtualizedEnabled, setVirtualizedEnabled] = useState(true);
  const [previewMode, setPreviewMode] = useState('hover'); // 'hover', 'click', 'disabled'
  const [autoLoadEnabled, setAutoLoadEnabled] = useState(false); // Désactivé par défaut
  const [itemHeight, setItemHeight] = useState(200);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [intelligentSortEnabled, setIntelligentSortEnabled] = useState(true);
  const [listLayout, setListLayout] = useState('airbnb'); // Vue carte style Airbnb
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [excludeOutOfStockLocal, setExcludeOutOfStockLocal] = useState(false); // Toggle filtre stock

  // Constantes pour la virtualisation - Hauteur optimisée pour compact avec marges
  const ITEM_HEIGHT = listLayout === 'compact' ? 170 : listLayout === 'comfortable' ? 240 : 320;
  const GRID_COLUMNS = isMobile ? 1 : listLayout === 'grid' ? 2 : 1;

  // Mémoriser les éléments pour la performance
  const listItems = useMemo(() => {
    console.log('[SearchResultsList] Results data:', results);
    console.log('[SearchResultsList] Listings array:', results.listings);
    return results.listings || [];
  }, [results.listings]);

  // 🛍️ Filtrer les produits selon l'état du toggle excludeOutOfStock
  const filteredProducts = useMemo(() => {
    let products = productResults?.products || [];

    if (filters.condition) {
      products = products.filter((product) => product.condition === filters.condition);
    }
    
    if (excludeOutOfStockLocal) {
      // Exclure les produits marqués comme out of stock
      products = products.filter(p => !p.isOutOfStock);
    }
    
    return products;
  }, [productResults?.products, filters.condition, excludeOutOfStockLocal]);

  const handleConditionQuickFilter = useCallback((condition) => {
    updateFilters({
      condition,
      _timestamp: Date.now()
    });
  }, [updateFilters]);

  // Auto-scroll vers le listing sélectionné depuis la carte (desktop uniquement)
  useEffect(() => {
    if (isMobile || !selectedListing || selectedListingSource !== 'map') return;

    const id = selectedListing.id;
    const el = cardRefs.current[id];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedListing, selectedListingSource, isMobile]);

  // Vérifier si un élément est chargé
  const isItemLoaded = useCallback((index) => {
    return index < listItems.length;
  }, [listItems.length]);

  // Charger plus d'éléments
  const loadMoreItems = useCallback(async () => {
    if (canLoadMore && !isLoading) {
      console.log('[SearchResultsList] Loading more items manually, current count:', listItems.length);
      
      try {
        await loadMore();
        console.log('[SearchResultsList] Successfully loaded more items, new count:', results.listings?.length);
      } catch (error) {
        console.error('[SearchResultsList] Error loading more items:', error);
      }
    }
  }, [canLoadMore, isLoading, loadMore, listItems.length, results.listings?.length]);

  // Gestionnaire de tri
  const handleSortChange = (newSortBy) => {
    const newSortOrder = sortBy === newSortBy && sortOrder === 'desc' ? 'asc' : 'desc';
    sortResults(newSortBy, newSortOrder);
    setShowSortOptions(false);
  };

  // Gestionnaire de sélection de listing
  const handleListingSelect = useCallback(async (listing) => {
    selectListing(listing);
    
    // Tracker l'interaction de sélection
    await trackInteraction({
      type: 'listing_selected',
      listingId: listing.id,
      position: results.listings.indexOf(listing),
      viewMode: viewMode,
      source: 'search_results_list'
    });
  }, [selectListing, trackInteraction, results.listings, viewMode]);

  // Gestionnaire de sélection produit (interaction avec la carte)
  const handleProductSelect = useCallback((product) => {
    // Adapter le produit pour que la carte puisse afficher son pin
    const lat = product.twinListing?.latitude ?? product.pickup_location?.lat ?? null;
    const lng = product.twinListing?.longitude ?? product.pickup_location?.lng ?? null;
    if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
      selectListing({ ...product, latitude: lat, longitude: lng, isProduct: true });
    }
  }, [selectListing]);

  // ======= NOUVELLES FONCTIONS RÉVOLUTIONNAIRES =======

  /**
   * Gestionnaire de changement de mode de vue avec analytics
   */
  const handleViewModeChange = useCallback(async (newViewMode) => {
    setViewMode(newViewMode);
    
    // Ajuster la hauteur des éléments selon le mode
    switch (newViewMode) {
      case 'compact':
        setItemHeight(120);
        break;
      case 'grid':
        setItemHeight(300);
        break;
      default:
        setItemHeight(200);
    }
    
    // Tracker le changement de vue
    await trackInteraction({
      type: 'view_mode_changed',
      oldViewMode: viewMode,
      newViewMode: newViewMode,
      source: 'search_results_controls'
    });
  }, [viewMode, trackInteraction]);

  /**
   * Tri intelligent basé sur les préférences utilisateur
   */
  const handleIntelligentSort = useCallback(async () => {
    if (!intelligentSortEnabled) return;
    
    try {
      // Tracker l'utilisation du tri intelligent
      await trackInteraction({
        type: 'intelligent_sort_used',
        currentSort: sortBy,
        resultCount: results.listings.length,
        source: 'search_results_controls'
      });
      
      // Appliquer un tri intelligent basé sur les métriques temps réel
      if (hasRealtimeMetrics && realtimeMetrics) {
        const intelligentSortBy = realtimeMetrics.popularCategories?.[0]?.category || 'rating';
        sortResults(intelligentSortBy, 'desc');
      } else {
        // Fallback vers tri par popularité
        sortResults('popularity', 'desc');
      }
    } catch (error) {
      console.error('[SearchResultsList] Error in intelligent sort:', error);
    }
  }, [intelligentSortEnabled, trackInteraction, sortBy, results.listings.length, hasRealtimeMetrics, realtimeMetrics, sortResults]);

  /**
   * Gestion du survol intelligent avec prévisualisation
   */
  const handleIntelligentHover = useCallback(async (listing, hoverDuration = 0) => {
    if (previewMode !== 'hover') return;
    
    // Sélectionner seulement si le survol dure plus de 500ms
    if (hoverDuration > 500) {
      selectListing(listing);
      
      // Tracker le survol intelligent
      await trackInteraction({
        type: 'intelligent_preview',
        listingId: listing.id,
        hoverDuration: hoverDuration,
        previewTrigger: 'hover',
        source: 'search_results_list'
      });
    }
  }, [previewMode, selectListing, trackInteraction]);

  /**
   * Chargement automatique intelligent - optimisé pour garantir le chargement des prochaines pages
   */
  const handleIntelligentAutoLoad = useCallback(async () => {
    if (!autoLoadEnabled || !canLoadMore || isLoading) return;
    
    try {
      console.log('[SearchResultsList] Intelligent auto load triggered, current count:', results.listings.length);
      
      // Tracker le chargement automatique
      await trackInteraction({
        type: 'auto_load_triggered',
        currentResultCount: results.listings.length,
        source: 'search_results_list'
      });
      
      // Forcer un délai court pour éviter les problèmes de timing
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Chargement des résultats suivants
      await loadMore();
      
      // Vérifier si le chargement a bien fonctionné en comparant les tailles avant/après
      console.log('[SearchResultsList] Auto load completed, new count:', results.listings.length);
      
      // Vérifier si nous devons charger davantage (si nous avons toujours peu d'éléments)
      if (results.listings.length < 15 && canLoadMore && !isLoading) {
        console.log('[SearchResultsList] Not enough items, loading more...');
        setTimeout(() => loadMoreItems(), 500);
      }
    } catch (error) {
      console.error('[SearchResultsList] Error in intelligent auto load:', error);
    }
  }, [autoLoadEnabled, canLoadMore, isLoading, trackInteraction, results.listings.length, loadMore, loadMoreItems]);

  /**
   * Optimisation de performance basée sur les métriques
   */
  const optimizePerformance = useCallback(() => {
    if (!hasRealtimeMetrics || !realtimeMetrics) return;
    
    const { performanceMetrics } = realtimeMetrics;
    
    if (performanceMetrics) {
      // Ajuster la virtualisation selon les performances
      if (performanceMetrics.averageResponseTime > 1000) {
        setVirtualizedEnabled(true);
        setItemHeight(150); // Réduire la hauteur pour améliorer les performances
      }
      
      // Désactiver les animations si les performances sont faibles
      if (performanceMetrics.averageResponseTime > 2000) {
        setAnimationsEnabled(false);
      }
    }
  }, [hasRealtimeMetrics, realtimeMetrics]);

  // ======= EFFECTS POUR LES NOUVELLES FONCTIONNALITÉS =======

  // Optimisation automatique des performances
  useEffect(() => {
    optimizePerformance();
  }, [optimizePerformance]);

  // Vérifier les annonces au démarrage
  useEffect(() => {
    // Débug: Vérifier l'état initial des annonces
    console.log('[SearchResultsList] Initial state check:');
    console.log('- listItems length:', listItems.length);
    console.log('- results.total:', results.total);
    console.log('- canLoadMore:', canLoadMore);
    
    // Ne pas charger automatiquement les annonces supplémentaires
    // pour laisser l'utilisateur contrôler quand charger plus d'annonces
  }, [listItems.length, results.total, canLoadMore]);

  // Chargement automatique des annonces suivantes quand il n'y en a pas assez - Désactivé
  useEffect(() => {
    // Cette fonctionnalité est désactivée pour éviter les problèmes avec le bouton de chargement manuel
    /*
    // Si on a moins de 10 annonces et qu'il en reste à charger, charger la page suivante automatiquement
    if (listItems.length > 0 && listItems.length < 10 && canLoadMore && !isLoading) {
      console.log('[SearchResultsList] Not enough items, auto-loading more:', listItems.length);
      setTimeout(() => loadMoreItems(), 500);
    }
    */
  }, [listItems.length, canLoadMore, isLoading, loadMoreItems]);

  // Chargement automatique intelligent au scroll - désactivé par défaut
  useEffect(() => {
    // On désactive le chargement automatique au scroll pour éviter les problèmes
    // avec le bouton "Charger plus"
    if (!autoLoadEnabled) return;
    
    // Le code ci-dessous est commenté pour empêcher le chargement automatique au scroll
    /*
    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      
      // Déclencher le chargement plus tôt, à 70% du scroll pour un chargement anticipé
      if (scrollPercentage > 0.7 && canLoadMore && !isLoading) {
        console.log('[SearchResultsList] Auto-loading triggered at scroll position:', Math.round(scrollPercentage * 100) + '%');
        handleIntelligentAutoLoad();
      }
    };
    
    const listContainer = document.querySelector('[data-testid="search-results-list"]');
    if (listContainer) {
      listContainer.addEventListener('scroll', handleScroll);
      return () => listContainer.removeEventListener('scroll', handleScroll);
    }
    */
  }, [autoLoadEnabled, handleIntelligentAutoLoad, canLoadMore, isLoading]);

  // Rendu d'un élément de la liste avec fonctionnalités révolutionnaires
  const renderListItem = useCallback(({ index, style }) => {
    const listing = listItems[index];
    
    if (!listing) {
      // Skeleton intelligent avec animations
      return (
        <div style={style}>
          <Box sx={{ p: 2 }}>
            {animationsEnabled ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Skeleton 
                  variant="rectangular" 
                  height={itemHeight - 32}
                  sx={{
                    borderRadius: 2,
                    transform: 'scale(1)',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    },
                    transition: 'transform 0.2s ease'
                  }}
                />
              </motion.div>
            ) : (
              <Skeleton variant="rectangular" height={itemHeight - 32} />
            )}
          </Box>
        </div>
      );
    }

    // Rendu avec animations et interactions intelligentes
    const cardComponent = (
      <SearchResultCard
        listing={listing}
        layout={listLayout}
        isSelected={selectedListing?.id === listing.id && !selectedListing?.isProduct}
        onSelect={() => handleListingSelect(listing)}
        onHover={(hoverDuration) => handleIntelligentHover(listing, hoverDuration)}
        previewMode={previewMode}
        animationsEnabled={animationsEnabled}
        enhancedFeatures={{
          showPerformanceMetrics: hasRealtimeMetrics,
          intelligentHighlighting: intelligentSortEnabled,
          adaptiveLayout: true
        }}
      />
    );

    return (
      <div style={style}>
        <Box sx={{ px: 2, py: 1, overflow: 'visible' }}>
          {animationsEnabled ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.3, 
                delay: index * 0.02,
                ease: "easeOut"
              }}
              style={{
                overflow: 'visible'
              }}
            >
              {cardComponent}
            </motion.div>
          ) : (
            cardComponent
          )}
        </Box>
      </div>
    );
  }, [
    listItems, 
    itemHeight, 
    animationsEnabled, 
    selectedListing, 
    handleListingSelect, 
    handleIntelligentHover, 
    viewMode, 
    previewMode, 
    hasRealtimeMetrics, 
    intelligentSortEnabled
  ]);

  // Options de tri
  const sortOptions = [
    { value: 'relevance', label: t('search.sort.relevance'), icon: '🎯' },
    { value: 'price', label: t('search.sort.price'), icon: '💰' },
    { value: 'rating', label: t('search.sort.rating'), icon: '⭐' },
    { value: 'date', label: t('search.sort.date'), icon: '📅' },
    { value: 'distance', label: t('search.sort.distance'), icon: '📍' }
  ];

  // Statistiques des résultats
  const resultsStats = useMemo(() => {
    const total = results.total || 0;
    const loaded = listItems.length;
    const hasMore = results.hasMore;
    
    return { total, loaded, hasMore };
  }, [results.total, results.hasMore, listItems.length]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" variant="outlined">
          {error.message || t('search.errors.general')}
        </Alert>
      </Box>
    );
  }

  if (!isLoading && listItems.length === 0 && !hasProductResults) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
          textAlign: 'center'
        }}
      >
        <Box
          component="img"
          src="/assets/empty-search.svg"
          alt="Aucun résultat"
          sx={{
            width: '100%',
            maxWidth: 200,
            height: 'auto',
            mb: 3,
            opacity: 0.6
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <Typography variant="h6" gutterBottom fontWeight={600}>
          {t('search.noResults.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('search.noResults.subtitle')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default'
      }}
    >
      {/* En-tête avec contrôles */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        {/* Statistiques des résultats - Version simplifiée */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            {/* Indicateur de chargement */}
            {isLoading && (
              <Chip
                label={t('search.loading.more')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>

        {/* Barre de contrôles moderne et compacte */}
        {searchType !== 'listings' && hasProductResults && (
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' }, 
            justifyContent: 'space-between',
            gap: { xs: 2, md: 1.5 },
            px: 2,
            py: { xs: 1.5, md: 1 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}08 30%, ${theme.palette.background.paper} 70%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 48
          }}>
            {/* Ligne 1 Mobile: Compteur + Tri */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: 1.5,
              width: { xs: '100%', md: 'auto' }
            }}>
              {/* Compteur */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  border: `1px solid ${theme.palette.divider}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}18 0%, ${theme.palette.secondary.main}12 100%)`,
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}14`,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  flexShrink: 0
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    letterSpacing: 0.4,
                    color: 'text.primary'
                  }}
                >
                  {filteredProducts.length}/{productResults?.products?.length || 0}
                </Typography>
              </Box>

              {/* Tri */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { xs: 'auto', md: 0 } }}>
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: { xs: 120, md: 140 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main + '10'
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ fontSize: '0.875rem' }}>{t('search.sort.label')}</InputLabel>
                  <Select
                    value={sortBy}
                    label={t('search.sort.label')}
                    onChange={(e) => handleSortChange(e.target.value)}
                  >
                    {sortOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{option.icon}</span>
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Tooltip title={sortOrder === 'asc' ? t('search.sort.desc', 'Décroissant') : t('search.sort.asc', 'Croissant')}>
                  <IconButton
                    size="small"
                    onClick={() => sortResults(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      '&:hover': { backgroundColor: theme.palette.primary.main + '15' }
                    }}
                  >
                    {sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Ligne 2 Mobile: État + Stock alignés ensemble */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'row', md: 'column' }, 
              alignItems: { xs: 'center', md: 'flex-end' }, 
              justifyContent: 'flex-start',
              gap: 1.5, 
              width: { xs: '100%', md: 'auto' },
              overflowX: { xs: 'auto', md: 'visible' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' }
            }}>
              {/* 🎛️ Toggle Condition */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>                
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    '& > *': {
                      margin: '0 !important'
                    }
                  }}
                >
                  <ToggleButtonGroup
                    value={filters.condition || 'all'}
                    exclusive
                    onChange={(event, newValue) => {
                      handleConditionQuickFilter(newValue === 'all' ? null : newValue);
                    }}
                    size="small"
                    sx={{
                      display: 'flex',
                      gap: '4px',
                      height: 32,
                      '& .MuiToggleButton-root': {
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        color: theme.palette.text.primary,
                        transition: 'all 0.2s ease',
                        margin: '0 !important',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          borderColor: theme.palette.primary.main,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="all" aria-label="all conditions">
                      {t('filters.all', 'Tous')}
                    </ToggleButton>
                    {PRODUCT_CONDITION_OPTIONS.map((option) => (
                      <ToggleButton
                        key={option.value}
                        value={option.value}
                        aria-label={option.value}
                      >
                        {t(`products:${option.translationKey}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* 🔘 Toggle Stock */}
              <Box 
               sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    border: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}18 0%, ${theme.palette.secondary.main}12 100%)`,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}14`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    flexShrink: 0
                  }}
                  >
              <FormControlLabel
                control={
                  <Switch
                    checked={excludeOutOfStockLocal}
                    onChange={(e) => setExcludeOutOfStockLocal(e.target.checked)}
                    color="success"
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      letterSpacing: 0.35,
                      color: 'text.primary'
                    }}
                  >
                    {t('search.product.excludeOutOfStock', 'Sans stock')}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Liste des résultats */}
      {searchType !== 'products' && (
      <Box 
        sx={{ 
          flex: 1,
          overflow: 'visible',
          position: 'relative',
          p: 2
        }}
        data-testid="search-results-list"
      >
        {listItems.length > 0 && (
          <AnimatePresence mode="wait">
            <Box
              key="airbnb-grid-list"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2
              }}
            >
              {listItems.map((listing) => (
                <div
                  key={listing.id}
                  ref={(el) => { cardRefs.current[listing.id] = el; }}
                >
                  <SearchResultCard
                    listing={listing}
                    layout="airbnb"
                    isSelected={selectedListing?.id === listing.id && !selectedListing?.isProduct}
                    onSelect={() => handleListingSelect(listing)}
                    onHover={(hoverDuration) => handleIntelligentHover(listing, hoverDuration)}
                  />
                </div>
              ))}
            </Box>
          </AnimatePresence>
        )}
      </Box>
      )}

      {/* ─── Liste des produits ─────────────────────────────────────────────── */}
      {searchType !== 'listings' && (
        <Box
          sx={{
            overflow: 'visible',
            px: 2,
            pt: 0
          }}
        >
          {!hasProductResults && !isLoading && searchType === 'products' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ShoppingCartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('search.product.noResults', 'Aucun produit disponible')}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2
            }}
          >
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                ref={(el) => { cardRefs.current[product.id] = el; }}
              >
                <SearchResultCard
                  listing={product}
                  layout="airbnb"
                  isProduct={true}
                  isSelected={selectedListing?.id === product.id && !!selectedListing?.isProduct}
                  onSelect={() => handleProductSelect(product)}
                />
              </div>
            ))}
          </Box>
        </Box>
      )}

      {/* Bouton "Charger plus" - visible seulement pour les listings */}
      {listItems.length > 0 && searchType !== 'products' && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          {/* Debug info - peut être retiré en production 
          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
              Debug: canLoadMore={canLoadMore.toString()}, isLoading={isLoading.toString()}, 
              hasMore={results.hasMore?.toString()}, total={results.total}, loaded={listItems.length}
            </Typography>
          )}*/}
          <Button
            variant="contained"
            color="primary"
            onClick={loadMore}
            disabled={isLoading || !canLoadMore}
            startIcon={
              isLoading ? <CircularProgress size={20} /> : 
              !canLoadMore ? <span>🎉</span> : null
            }
            sx={{ 
              minWidth: 200,
              fontWeight: 'bold',
              py: 1.2,
              px: 3,
              fontSize: '1rem',
              boxShadow: !canLoadMore ? 1 : 3,
              opacity: !canLoadMore ? 0.7 : 1,
              '&:hover': {
                boxShadow: !canLoadMore ? 1 : 5,
                bgcolor: !canLoadMore ? 'primary.main' : 'primary.dark'
              },
              '&.Mui-disabled': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                opacity: 0.7
              }
            }}
          >
            {isLoading 
              ? t('search.loading') 
              : !canLoadMore 
                ? t('search.all_loaded') 
                : t('search.load_more')
            }
            ({listItems.length} / {results.total || '?'})
          </Button>
          
        </Box>
      )}
    </Box>
  );
};

export default SearchResultsList;